// Whether this browser's wallet can vouch on behalf of the issuer a claim names,
// and if not, why, in plain words. Only delegations from that issuer are looked
// at: a delegation from any other issuer is never offered or mentioned.
import type { Delegation } from "../wallet/wallet";

export type NoteReason = "out_of_scope" | "expired" | "not_yet_valid";
export interface Note { delegation: Delegation; reason: NoteReason; claimType: string }

export function delegationChoices(delegations: Delegation[], namedIssuer: string | undefined, claimType: string,
                                  now = Date.now()): { offer: Delegation[]; notes: Note[] } {
  if (!namedIssuer) return { offer: [], notes: [] };
  const offer: Delegation[] = [], notes: Note[] = [];
  for (const d of delegations.filter((x) => x.issuer === namedIssuer)) {
    if (d.validFrom && now < Date.parse(d.validFrom)) notes.push({ delegation: d, reason: "not_yet_valid", claimType });
    else if (d.validUntil && now > Date.parse(d.validUntil)) notes.push({ delegation: d, reason: "expired", claimType });
    else if (!d.scope.includes(claimType)) notes.push({ delegation: d, reason: "out_of_scope", claimType });
    else offer.push(d);
  }
  return offer.length ? { offer, notes: [] } : { offer, notes };
}

const list = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
const date = (iso: string, locale?: string) =>
  new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

export function explain(note: Note, names: { issuer: string; claimTypes: Record<string, string> }, locale?: string): string {
  const d = note.delegation, who = names.issuer;
  const tail = `so you can vouch as yourself, not on ${who}'s behalf.`;
  if (note.reason === "expired") return `Your ${who} delegation ended on ${date(d.validUntil!, locale)}, ${tail}`;
  if (note.reason === "not_yet_valid") return `Your ${who} delegation starts on ${date(d.validFrom!, locale)}, ${tail}`;
  const label = (t: string) => names.claimTypes[t] ?? t;
  return `Your ${who} delegation covers ${list(d.scope.map(label))}. This claim is a ${label(note.claimType)}, ${tail}`;
}
